'use client'

import { ReactNode, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, ArrowDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DemoRequestDialog } from '@/components/platform/v2/DemoRequestDialog'
import { PRODUCT_SECTIONS, getSection } from '@/data/productNav'
import { CASES } from '@/data/cases'
import StatusPill from '@/components/platform/v2/StatusPill'
import { PageCta, PageHero } from '@/components/platform/v2/PageEdge'
import { Status } from '@/data/platform'
import { SectionHeading } from '@/components/platform/v2/Section'

/* ------------------------------------------------------------------ hero */

interface HeroProps {
    eyebrow: string
    titleLines: string[]
    accentLine?: number
    lead: string
    primary: { label: string; to?: string; onClick?: () => void }
    secondary?: { label: string; to?: string; onClick?: () => void }
    visual?: ReactNode
    /** узкий hero без визуала — по центру */
    centered?: boolean
}

export const ProductHero = ({
    eyebrow,
    titleLines,
    accentLine = titleLines.length - 1,
    lead,
    primary,
    secondary,
    visual,
    centered,
}: HeroProps) => (
    <PageHero
        eyebrow={eyebrow}
        title={
            <>
                {titleLines.map((line, i) => (
                    <span key={line} className={cn('block', i === accentLine && 'text-primary')}>
                        {line}
                    </span>
                ))}
            </>
        }
        text={lead}
        actions={
            <>
                {primary.to ? (
                    <Link href={primary.to} className="btn-primary w-full sm:w-auto">
                        {primary.label}
                    </Link>
                ) : (
                    <button onClick={primary.onClick} className="btn-primary w-full sm:w-auto">
                        {primary.label}
                    </button>
                )}
                {secondary &&
                    (secondary.to ? (
                        <Link href={secondary.to} className="btn-ghost w-full sm:w-auto">
                            {secondary.label} <ArrowRight className="h-4 w-4" />
                        </Link>
                    ) : (
                        <button onClick={secondary.onClick} className="btn-ghost w-full sm:w-auto">
                            {secondary.label}
                        </button>
                    ))}
            </>
        }
        visual={centered ? undefined : visual}
        contentClassName={centered ? 'lg:grid-cols-1' : undefined}
    />
)

/* --------------------------------------------------------------- headings */

export const Lead = ({ children, className }: { children: ReactNode; className?: string }) => (
    <p className={cn('lede', className)}>{children}</p>
)

export const Statement = ({ children, note, tone = 'dark' }: { children: ReactNode; note?: string; tone?: 'dark' | 'light' }) => (
    <div
        className={cn(
            'rounded-[1.75rem] border px-6 py-12 text-center sm:px-10 sm:py-16',
            tone === 'dark'
                ? 'border-primary/25 bg-[hsl(var(--charcoal))]'
                : 'border-transparent bg-[hsl(var(--warm-light))] text-[hsl(var(--deep-black))]',
        )}
    >
        <p
            className={cn(
                'mx-auto max-w-3xl text-2xl font-extrabold leading-tight tracking-[-0.02em] sm:text-3xl lg:text-4xl',
                tone === 'light' && 'text-[hsl(var(--deep-black))]',
            )}
        >
            {children}
        </p>
        {note && (
            <p className={cn('mt-4 text-sm', tone === 'dark' ? 'text-muted-foreground' : 'text-[hsl(var(--deep-black))]/60')}>{note}</p>
        )}
    </div>
)

/* ------------------------------------------------------------------ flows */

/** Цепочка шагов: на десктопе горизонтально, на мобильном — сверху вниз. */
export const FlowStrip = ({ steps, compact }: { steps: { title: string; note?: string; accent?: boolean }[]; compact?: boolean }) => (
    <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
        {steps.map((s, i) => (
            <div key={s.title} className="flex min-w-0 flex-1 flex-col gap-3 md:flex-row md:items-center">
                <div
                    className={cn(
                        'flex-1 rounded-2xl border px-4 py-4',
                        s.accent ? 'border-primary/50 bg-primary/10' : 'border-border/60 bg-[hsl(var(--charcoal))]',
                        compact && 'py-3',
                    )}
                >
                    <p className={cn('text-sm font-bold', s.accent ? 'text-primary' : 'text-foreground')}>{s.title}</p>
                    {s.note && <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{s.note}</p>}
                </div>
                {i < steps.length - 1 && (
                    <>
                        <ArrowDown className="mx-auto h-4 w-4 shrink-0 text-primary/70 md:hidden" />
                        <ArrowRight className="hidden h-4 w-4 shrink-0 text-primary/70 md:block" />
                    </>
                )}
            </div>
        ))}
    </div>
)

/** Вертикальный нумерованный сценарий. */
export const ScenarioList = ({ steps }: { steps: { title: string; text?: string }[] }) => (
    <ol className="relative flex flex-col gap-4 pl-8">
        <span className="absolute left-[13px] top-2 bottom-2 w-px bg-gradient-to-b from-primary/60 via-border to-transparent" />
        {steps.map((s, i) => (
            <li key={s.title} className="relative">
                <span className="absolute -left-8 top-0 flex h-7 w-7 items-center justify-center rounded-full border border-primary/40 bg-[hsl(var(--background))] text-xs font-bold text-primary">
                    {i + 1}
                </span>
                <p className="text-sm font-bold text-foreground sm:text-base">{s.title}</p>
                {s.text && <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>}
            </li>
        ))}
    </ol>
)

export const CheckList = ({ items, className }: { items: string[]; className?: string }) => (
    <ul className={cn('flex flex-col gap-2', className)}>
        {items.map((i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-foreground/85">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                {i}
            </li>
        ))}
    </ul>
)

export const FeatureCard = ({
    title,
    text,
    items,
    status,
    className,
}: {
    title: string
    text?: string
    items?: string[]
    status?: Status
    className?: string
}) => (
    <article className={cn('surface surface-hover flex flex-col gap-3', className)}>
        <div className="flex items-start justify-between gap-3">
            <h3 className="text-lg font-bold text-foreground sm:text-xl">{title}</h3>
            {status && <StatusPill status={status} />}
        </div>
        {text && <p className="body-text">{text}</p>}
        {items && <CheckList items={items} className="mt-auto pt-1" />}
    </article>
)

/* --------------------------------------------------------- platform links */

/** Визуальная карта платформы. */
export const PlatformMap = ({ activeTo }: { activeTo?: string }) => (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PRODUCT_SECTIONS.filter((s) => s.to !== activeTo).map((s) => (
            <Link
                key={s.to}
                href={s.to}
                className="group relative flex flex-col gap-2 rounded-2xl border border-border/60 bg-[hsl(var(--deep-black))] p-5 transition-all hover:border-primary/50 hover:bg-[hsl(var(--charcoal))]"
            >
                <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary/80">{s.role}</span>
                <span className="text-base font-bold text-foreground">{s.label}</span>
                <span className="text-sm text-muted-foreground">{s.essence}</span>
                <ArrowRight className="mt-2 h-4 w-4 text-primary transition-transform group-hover:translate-x-1" />
            </Link>
        ))}
    </div>
)

/** Логичный следующий шаг в конце раздела. */
export const NextSteps = ({
    title = 'Дальше по контуру',
    items,
}: {
    title?: string
    items: { to: string; label?: string; text: string }[]
}) => (
    <div className="flex flex-col gap-6">
        <SectionHeading eyebrow="Следующий шаг" title={title} />
        <div className="grid gap-4 md:grid-cols-2">
            {items.map((i) => {
                const section = getSection(i.to)
                return (
                    <Link
                        key={i.to}
                        href={i.to}
                        className="group flex flex-col gap-2 rounded-2xl border border-border/60 bg-[hsl(var(--charcoal))] p-6 transition-all hover:border-primary/50"
                    >
                        <span className="text-sm text-muted-foreground">{i.text}</span>
                        <span className="flex items-center gap-2 text-lg font-extrabold text-foreground">
                            {i.label ?? section?.label}
                            <ArrowRight className="h-4 w-4 text-primary transition-transform group-hover:translate-x-1" />
                        </span>
                    </Link>
                )
            })}
        </div>
    </div>
)

/* -------------------------------------------------------------- CTA / кейсы */

export const ProductCta = ({
    title,
    text,
    buttonLabel,
    to,
    dialogTitle,
    secondary,
    visual,
}: {
    title: ReactNode
    text?: string
    buttonLabel: string
    to?: string
    dialogTitle?: string
    secondary?: { to: string; label: string }
    visual?: ReactNode
}) => {
    const [open, setOpen] = useState(false)
    return (
        <>
            <PageCta
                title={title}
                text={text}
                actions={
                    <>
                        {to ? (
                            <Link href={to} className="btn-primary w-full sm:w-auto">
                                {buttonLabel}
                            </Link>
                        ) : (
                            <button onClick={() => setOpen(true)} className="btn-primary w-full sm:w-auto">
                                {buttonLabel}
                            </button>
                        )}
                        {secondary && (
                            <Link href={secondary.to} className="btn-ghost w-full sm:w-auto">
                                {secondary.label} <ArrowRight className="h-4 w-4" />
                            </Link>
                        )}
                    </>
                }
                visual={visual}
            />
            {!to && <DemoRequestDialog open={open} onOpenChange={setOpen} title={dialogTitle ?? buttonLabel} />}
        </>
    )
}

/** 1–2 релевантных реальных кейса. */
export const RelatedCases = ({ slugs, text }: { slugs: string[]; text?: string }) => {
    const items = slugs.map((s) => CASES.find((c) => c.slug === s)).filter(Boolean)
    if (!items.length) return null
    return (
        <div className="flex flex-col gap-6">
            <SectionHeading eyebrow="Как это выглядит в жизни" title="Кейсы" text={text} />
            <div className="grid gap-4 md:grid-cols-2">
                {items.map((c) => (
                    <Link
                        key={c!.slug}
                        href="/platform/cases"
                        className="group flex flex-col gap-3 rounded-2xl border border-border/60 bg-[hsl(var(--deep-black))] p-6 transition-all hover:border-primary/50"
                    >
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/12 text-xs font-extrabold text-primary">
                                {c!.marker}
                            </span>
                            <div className="min-w-0">
                                <p className="truncate text-base font-bold text-foreground">{c!.client}</p>
                                <p className="truncate text-xs text-muted-foreground">{c!.category}</p>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{c!.shortDescription}</p>
                        <span className="btn-link mt-auto">
                            Смотреть кейс <ArrowRight className="h-4 w-4" />
                        </span>
                    </Link>
                ))}
            </div>
        </div>
    )
}
